import React, { useState, useEffect, Suspense, memo } from 'react';
import { getCurrentUser, logoutDistributor } from './services/authService';
import { logAnalyticsEvent } from './config/firebase';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { 
  CssBaseline, 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Button,
  Container,
  Box,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Fade,
  Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import { useMediaQuery } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import LoginIcon from '@mui/icons-material/Login';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Map as MapIcon,
  Close as CloseIcon,
  NearMe as NearMeIcon,
  Directions as DirectionsIcon,
  Store as StoreIcon,
  Directions as DirectionsCarIcon,
  Support as SupportIcon
} from '@mui/icons-material';

// Change static imports to lazy imports
const StoreList = React.lazy(() => import('./components/StoreList/StoreList'));
const DistributorPanel = React.lazy(() => import('./components/DistributorPanel/DistributorPanel'));
const DistributorView = React.lazy(() => import('./components/DistributorView/DistributorView'));
const DistributorAuth = React.lazy(() => import('./components/DistributorAuth/DistributorAuth'));
const Login = React.lazy(() => import('./components/Login/Login'));

// Import user service for role checking
import { getUserProfile, USER_ROLES } from './services/userService';

// Create rtl cache
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
  prepend: true  // This ensures our RTL styles take precedence
});

// Create RTL theme
const theme = createTheme({
  direction: 'rtl',
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: 'Cairo, Roboto, Helvetica, Arial, sans-serif',
  },
  components: {
    MuiPopover: {
      defaultProps: {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 500,
        },
      },
    },
  },
});

// Memoize the loader component
const ViewTransitionLoader = memo(() => {
  const { t } = useTranslation();
  
  return (
    <Fade in={true} timeout={200}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body1" color="text.secondary">
            {t('common.loading')}
          </Typography>
        </Box>
      </Box>
    </Fade>
  );
});

function App() {
  const [isDistributorView, setIsDistributorView] = useState(() => {
    // Check if URL path is /distributor or localStorage flag is true
    return window.location.pathname === '/distributor' || localStorage.getItem('isDistributorView') === 'true';
  });
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'admin' or 'distributor'
  const [userApproved, setUserApproved] = useState(true);
  const [loading, setLoading] = useState(true);
  const [viewTransitioning, setViewTransitioning] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [controlPanelAnchorEl, setControlPanelAnchorEl] = useState(null);
  const [selectedSection, setSelectedSection] = useState('add');

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    checkAuth();
    // Log page view
    logAnalyticsEvent('page_view', {
      page_title: isDistributorView ? 'distributor_panel' : 'store_locator',
      page_location: window.location.href,
      page_path: window.location.pathname
    });
  }, []);

  const checkAuth = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      if (currentUser) {
        const profile = await getUserProfile(currentUser.uid);
        setUserRole(profile?.role || USER_ROLES.DISTRIBUTOR);
        setUserApproved(profile?.isApproved !== false);
      }
    } catch (error) {
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageSelect = (lang) => {
    i18n.changeLanguage(lang);
    setAnchorEl(null);
    document.dir = lang === 'ar' ? 'rtl' : 'ltr';
    // Log language change
    logAnalyticsEvent('language_change', {
      language: lang
    });
  };

  const handleLogout = async () => {
    try {
      await logoutDistributor();
      setUser(null);
      setUserRole(null);
      setUserApproved(true);
      setIsDistributorView(false);
      localStorage.removeItem('isDistributorView');
      logAnalyticsEvent('user_logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleDistributorAuthSuccess = async (profile) => {
    const currentUser = await getCurrentUser();
    setUser(currentUser);
    setUserRole(profile?.role || USER_ROLES.DISTRIBUTOR);
    setUserApproved(profile?.isApproved !== false);
  };

  const handleControlPanelClick = (event) => {
    setControlPanelAnchorEl(event.currentTarget);
  };

  const handleControlPanelClose = () => {
    setControlPanelAnchorEl(null);
  };

  const handleViewTransition = async (newView, section = null) => {
    setViewTransitioning(true);
    setIsDistributorView(newView);
    localStorage.setItem('isDistributorView', newView.toString());
    if (section) {
      setSelectedSection(section);
    }
    // Log view change
    logAnalyticsEvent('view_change', {
      from_view: isDistributorView ? 'distributor_panel' : 'store_locator',
      to_view: newView ? 'distributor_panel' : 'store_locator',
      section: section
    });
    await new Promise(resolve => setTimeout(resolve, 300));
    setViewTransitioning(false);
  };

  const handleHomeClick = () => {
    handleViewTransition(false);
    setMobileMenuOpen(false);
  };

  const handleLoginClick = () => {
    handleViewTransition(true);
    setMobileMenuOpen(false);
  };

  const handleSectionSelect = (section) => {
    handleViewTransition(true, section);
    setControlPanelAnchorEl(null);
    setMobileMenuOpen(false);
    
    // Add a delay to ensure the section is rendered before scrolling
    setTimeout(() => {
      const sectionHeader = document.getElementById(`${section}-section-header`);
      if (sectionHeader) {
        const headerOffset = 20;
        const elementPosition = sectionHeader.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 500); // Increased delay to ensure content is fully rendered
  };

  // Pending approval component
  const PendingApproval = () => (
    <Box sx={{ p: 4, textAlign: 'center', maxWidth: 500, mx: 'auto', mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom color="warning.main">⏳ {t('common.auth.pendingApproval')}</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>{t('common.auth.pendingApprovalDesc')}</Typography>
        <Button variant="outlined" onClick={handleLogout}>{t('common.navigation.logout')}</Button>
      </Paper>
    </Box>
  );

  // Wrap the main content render with Suspense
  const renderContent = () => (
    <Suspense fallback={<ViewTransitionLoader />}>
      <Fade in={!viewTransitioning} timeout={300}>
        <Box>
          {isDistributorView ? (
            user ? (
              userRole === USER_ROLES.ADMIN ? (
                <DistributorPanel initialSection={selectedSection} />
              ) : userApproved ? (
                <DistributorView />
              ) : (
                <PendingApproval />
              )
            ) : (
              <DistributorAuth onAuthSuccess={handleDistributorAuthSuccess} />
            )
          ) : (
            <StoreList />
          )}
        </Box>
      </Fade>
    </Suspense>
  );

  if (loading) {
    return <ViewTransitionLoader />;
  }

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div dir="rtl">
          {viewTransitioning && <ViewTransitionLoader />}
          <Box sx={{ flexGrow: 1 }}>
            <AppBar 
              position="static" 
              elevation={0}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(8px)',
                borderBottom: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05), 0 1px 3px 0 rgba(0,0,0,0.08)',
                position: 'relative',
                zIndex: 1100,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 100%)',
                  zIndex: -1
                }
              }}
            >
              <Toolbar 
                sx={{ 
                  minHeight: '70px',
                  px: { xs: 2, sm: 4 },
                  gap: 1
                }}
              >
                {isMobile && (
                  <IconButton
                    color="primary"
                    edge="start"
                    onClick={() => setMobileMenuOpen(true)}
                    sx={{ mr: 2 }}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
                
                <Typography 
                  variant="h6" 
                  component="div" 
                  sx={{ 
                    flexGrow: 1,
                    color: 'text.primary',
                    fontWeight: 600
                  }}
                >
                  {t('stores.title')}
                </Typography>

                {!isMobile && (
                  <>
                    <Button
                      color="primary"
                      startIcon={<HomeIcon />}
                      onClick={handleHomeClick}
                      sx={{ 
                        mx: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)'
                        }
                      }}
                    >
                      {t('common.navigation.home')}
                    </Button>
                    
                    {user ? (
                      <>
                        {/* Show Control Panel dropdown only for admin */}
                        {userRole === USER_ROLES.ADMIN ? (
                          <>
                            <Button 
                              color="primary"
                              startIcon={<BusinessIcon />}
                              endIcon={<ArrowDropDownIcon />}
                              onClick={handleControlPanelClick}
                              sx={{ 
                                mx: 1,
                                px: 2,
                                py: 1,
                                borderRadius: 2,
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.08)'
                                },
                                '& .MuiButton-endIcon': {
                                  ml: 1,
                                  transition: 'transform 0.2s',
                                  transform: Boolean(controlPanelAnchorEl) ? 'rotate(180deg)' : 'rotate(0)'
                                },
                                '& .MuiButton-startIcon': {
                                  mr: 1
                                }
                              }}
                            >
                              {t('common.navigation.controlPanel')}
                            </Button>
                            <Menu
                              anchorEl={controlPanelAnchorEl}
                              open={Boolean(controlPanelAnchorEl)}
                              onClose={handleControlPanelClose}
                              PaperProps={{
                                elevation: 2,
                                sx: {
                                  mt: 1,
                                  width: 180,
                                  borderRadius: 2,
                                  '& .MuiMenuItem-root': {
                                    py: 1.5,
                                    px: 2,
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    '&:hover': {
                                      backgroundColor: 'rgba(25, 118, 210, 0.08)'
                                    }
                                  }
                                }
                              }}
                            >
                              <MenuItem 
                                onClick={() => handleSectionSelect('add')}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5
                                }}
                              >
                                <Typography sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  flex: 1
                                }}>
                                  {t('common.distributor.addNewStore')}
                                </Typography>
                                <AddIcon color="primary" fontSize="small" />
                              </MenuItem>
                              <Divider />
                              <MenuItem 
                                onClick={() => handleSectionSelect('search')}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5
                                }}
                              >
                                <Typography sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  flex: 1
                                }}>
                                  {t('common.distributor.searchStores')}
                                </Typography>
                                <SearchIcon color="primary" fontSize="small" />
                              </MenuItem>
                              <Divider />
                              <MenuItem 
                                onClick={() => handleSectionSelect('reports')}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5
                                }}
                              >
                                <Typography sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  flex: 1
                                }}>
                                  {t('common.distributor.reports')}
                                </Typography>
                                <AssessmentIcon color="primary" fontSize="small" />
                              </MenuItem>
                            </Menu>
                          </>
                        ) : (
                          /* Show simple Dashboard button for distributors */
                          <Button 
                            color="primary"
                            startIcon={<BusinessIcon />}
                            onClick={() => handleViewTransition(true)}
                            sx={{ 
                              mx: 1,
                              px: 2,
                              py: 1,
                              borderRadius: 2,
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.08)'
                              }
                            }}
                          >
                            {t('common.distributor.distributorPortal')}
                          </Button>
                        )}
                        <IconButton 
                          color="primary" 
                          onClick={handleLogout}
                          title={t('common.navigation.logout')}
                          sx={{
                            ml: 1,
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.15)'
                            }
                          }}
                        >
                          <LogoutIcon />
                        </IconButton>
                      </>
                    ) : (
                      <Button
                        color="primary"
                        startIcon={<LoginIcon />}
                        onClick={handleLoginClick}
                        sx={{ 
                          mx: 1,
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)'
                          }
                        }}
                      >
                        {t('common.navigation.login')}
                      </Button>
                    )}
                  </>
                )}

                <IconButton
                  color="primary"
                  onClick={handleLanguageMenu}
                  sx={{ 
                    ml: 1,
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.15)'
                    }
                  }}
                  title={t('common.navigation.changeLanguage')}
                >
                  <LanguageIcon />
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  PaperProps={{
                    elevation: 2,
                    sx: {
                      mt: 1,
                      minWidth: 180,
                      borderRadius: 2,
                      '& .MuiMenuItem-root': {
                        py: 1.5
                      }
                    }
                  }}
                >
                  <MenuItem onClick={() => handleLanguageSelect('en')}>{t('common.language.english')}</MenuItem>
                  <MenuItem onClick={() => handleLanguageSelect('ar')}>{t('common.language.arabic')}</MenuItem>
                </Menu>
              </Toolbar>
            </AppBar>

            <Drawer
              anchor="right"
              open={mobileMenuOpen}
              onClose={() => setMobileMenuOpen(false)}
            >
              <Box sx={{ width: 250, pt: 2 }}>
                <List>
                  <ListItem>
                    <ListItemButton onClick={() => {
                      setIsDistributorView(false);
                      setMobileMenuOpen(false);
                    }}>
                      <ListItemIcon><HomeIcon /></ListItemIcon>
                      <ListItemText primary={t('common.navigation.home')} />
                    </ListItemButton>
                  </ListItem>
                  
                  {user ? (
                    <>
                      <ListItem>
                        <ListItemButton onClick={() => {
                          setIsDistributorView(true);
                          setMobileMenuOpen(false);
                        }}>
                          <ListItemIcon><BusinessIcon /></ListItemIcon>
                          <ListItemText primary={t('common.navigation.controlPanel')} />
                        </ListItemButton>
                      </ListItem>
                      <ListItem>
                        <ListItemButton onClick={handleLogout}>
                          <ListItemIcon><LogoutIcon /></ListItemIcon>
                          <ListItemText primary={t('common.navigation.logout')} />
                        </ListItemButton>
                      </ListItem>
                    </>
                  ) : (
                    <ListItem>
                      <ListItemButton onClick={() => {
                        setIsDistributorView(true);
                        setMobileMenuOpen(false);
                      }}>
                        <ListItemIcon><LoginIcon /></ListItemIcon>
                        <ListItemText primary={t('common.navigation.login')} />
                      </ListItemButton>
                    </ListItem>
                  )}
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <ListItem>
                    <ListItemButton 
                      component="a" 
                      href="https://alfursanpro.com/" 
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ListItemIcon><SupportIcon /></ListItemIcon>
                      <ListItemText primary={t('common.navigation.technicalSupport')} />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Box>
            </Drawer>

            <Container maxWidth="lg" sx={{ mt: 4 }}>
              {renderContent()}
            </Container>
          </Box>
        </div>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;